import {
  useGetAccountMembersQuery,
  useGetCampaignDetailsQuery,
  useGetCampaignSummaryQuery,
} from "api";
import { QUERY_SEPARATOR } from "constant";
import {
  useAppSelector,
  useDebounceValue,
  useHandleError,
  useStateWithSearchParams,
} from "hooks";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AccountPlanStatus, ColumnType } from "../../../types/enums";
import { Box, CircularProgress, Stack } from "@mui/material";
import { InputWithQuery } from "../../ui/InputWithQuery";
import { DropdownWithQuery } from "../../ui/dropdown/DropdownWithQuery";
import { CustomCalendar } from "components/ui/calendar";
import { ResetAllFiltersButton } from "../../features/ResetAllFiltersButton";
import { OpenModalButton, ToTopBtn } from "../../ui/buttons";
import {
  AccountPlanCard,
  InfiniteScroll,
  NoContentBlock,
  TableTabs,
} from "../../ui";
import { MemberPlaybookItem, WithId } from "types/apiTypes";
import { ExportAccountDataBtn } from "../../ui/export-data";
import { GeneratePlaybooksModal } from "../../ui/modal";

type SearchParams = {
  search?: string;
  date?: string;
  status_filter?: string;
};

const statusFilterList = ["All", "Ready", "Missing"];

const statusFilterMapper: Record<string, string | undefined> = {
  All: undefined,
  Ready: AccountPlanStatus.completed,
  "Needs Update": AccountPlanStatus.needs_update,
  Missing: AccountPlanStatus.ready,
};

const PER_PAGE = 25;

export const PlaybookReports = () => {
  const { campaign_id, account_id } = useParams();
  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const [page, setPage] = useState<number>(0);
  const [columnsType, setColumnsType] = useState<ColumnType>(ColumnType.grid);

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    search: undefined,
    date: undefined,
    status_filter: undefined,
  });

  const debounceSearch = useDebounceValue(searchDict.search);

  const { data, isFetching, error } = useGetAccountMembersQuery(
    {
      account_id: account_id!,
      search: debounceSearch,
      page: page,
      per_page: PER_PAGE,
      status_filter: !isEditor
        ? AccountPlanStatus.completed
        : (statusFilterMapper[
            searchDict?.status_filter || "All"
          ] as AccountPlanStatus),
      start_date: searchDict.date?.split(QUERY_SEPARATOR)[0],
      end_date: searchDict.date?.split(QUERY_SEPARATOR)[1],
    },
    {
      skip: !account_id || isEditor === undefined,
      refetchOnMountOrArgChange: true,
    },
  );

  useHandleError(error);

  const { data: summary } = useGetCampaignSummaryQuery({
    campaign_id: campaign_id!,
  });

  useEffect(() => {
    setPage(0);
  }, [debounceSearch, searchDict?.status_filter]);

  const isFilters = searchDict.date || searchDict?.status_filter;

  const isGrid = ColumnType.grid === columnsType;

  const canLoadMore = page + 1 < (data?.total_pages || 0);

  const transformData = useMemo(
    () =>
      (data?.items || []).map((el) => ({
        ...el,
        id: el.member_id,
      })),
    [data?.items],
  );

  const { data: details } = useGetCampaignDetailsQuery({
    account_id: account_id!,
  });

  if (!details) return null;

  const { total_members, total_members_with_playbook } = details;

  return (
    <Stack gap="12px" flexGrow={1}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <TableTabs isGrid={isGrid} setColumnsType={setColumnsType} />
        <InputWithQuery />
        {isEditor && (
          <Stack>
            <DropdownWithQuery
              query={"status_filter"}
              label={""}
              items={statusFilterList}
              placeholder={""}
              initSelected={statusFilterList[0]}
              maxHeight="134px"
              maxItemsWidth={"172px"}
            />
          </Stack>
        )}

        <Stack flexDirection={"row"} gap="12px">
          <CustomCalendar date={searchDict.date} height={"42px"} />
          {isFilters && <ResetAllFiltersButton />}
        </Stack>

        {summary && (
          <Stack
            direction="row"
            gap="12px"
            marginLeft="auto"
            alignItems="center"
          >
            {total_members_with_playbook > 0 && (
              <ExportAccountDataBtn
                total_members_with_playbook={total_members_with_playbook}
                account_id={account_id!}
                campaign_id={campaign_id!}
                is_analyzed={false}
              />
            )}

            {total_members_with_playbook !== total_members && isEditor && (
              <OpenModalButton
                isGradient={true}
                btnText={"Generate Playbooks"}
                renderModal={({ isOpen, onClose, setIsLoading }) => (
                  <GeneratePlaybooksModal
                    isOpen={isOpen}
                    onClose={onClose}
                    setIsLoading={setIsLoading}
                  />
                )}
              />
            )}
          </Stack>
        )}
      </Box>
      <InfiniteScroll<MemberPlaybookItem & WithId>
        hasMore={canLoadMore}
        isLoading={isFetching}
        loadMore={() => setPage((prev) => prev + 1)}
        page={page}
        items={transformData}
        scrollOffset={1200}
        showScrollTopOffset={100}
        startPage={0}
      >
        {({ showScrollTop, items }) => (
          <>
            {isFetching && page === 0 ? (
              <Box
                display="flex"
                height="25vh"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress size={80} sx={{ color: "#FE8836" }} />
              </Box>
            ) : (
              <>
                {items.length === 0 &&
                !isFetching &&
                (isFilters || !!searchDict.search) ? (
                  <Stack
                    gap="8px"
                    alignItems="center"
                    justifyContent="center"
                    padding="32px"
                  >
                    <NoContentBlock
                      title={"No Matching SDR Playbooks"}
                      text={
                        "No playbooks match your search. Please try another keyword."
                      }
                    />
                  </Stack>
                ) : (
                  <Stack
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: `repeat(${isGrid ? 2 : 1}, 1fr)`,
                        lg: `repeat(${isGrid ? 3 : 1}, 1fr)`,
                      },
                      gap: "12px",
                    }}
                  >
                    {items.map(
                      ({
                        member_id,
                        member_name,
                        updated_at,
                        status,
                        is_new,
                      }) => (
                        <AccountPlanCard
                          key={account_id}
                          id={member_id}
                          campaign_id={campaign_id!}
                          name={member_name}
                          updatedAt={updated_at}
                          isGrid={isGrid}
                          isNeedsUpdate={false}
                          status={status}
                          isNew={is_new}
                          isPlaybook={true}
                          isEditor={isEditor}
                        />
                      ),
                    )}

                    {showScrollTop && <ToTopBtn />}
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </InfiniteScroll>
      {isFetching && page !== 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={"8vh"}
        >
          <CircularProgress size={30} sx={{ color: "#FE8836" }} />
        </Box>
      )}
    </Stack>
  );
};
